import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { Link } from '@mui/material';
import ModeEditIcon from '@mui/icons-material/ModeEdit';

import { ObjectTypes, SortDirection } from '../../utils/constants';
import { countrySearchActions, countrySliceName, filterFields } from './store/slices/countrySearchSlice';
import {
  doSearchStoreActionHandler,
  loadGridColumnSettingStoreActionHandler,
  loadSavedFilterStoreActionHandler,
} from './store/actions/countrySearchActions';
import {
  PermissionCodes,
  PermissionRights,
} from '../../components/Guards/PermissionGuard/PermissionGuard';
import IF from '../../components/ui/IF';
import SearchPage from '../../components/SearchPage/SearchPage';
import AddNewCountryPopover from './components/AddNewCountryPopover/AddNewCountryPopover';
import useAuth from '../../hooks/useAuth';
import { searchCountryWithPagination } from '../../services/api/CountryApi';

const columnMappingFormatter = {
  Action: ({ row }) => (
    <Link
      component={RouterLink} to={`/country/${row['CountryCode']}/edit`} underline="none"
      sx={{ ml: 1 }} variant="subtitle2"
    >
      <ModeEditIcon fontSize="small" color="action" />
    </Link>
  )
};

const CountrySearch = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isAuthorized } = useAuth();

  const { filter, tableInfo } = useSelector((state) => state.countrySearch);

  const allowToAdd = isAuthorized({
    permissionCode: PermissionCodes.List,
    right: PermissionRights.Add,
  });
  const breadcrumbsLinks = useMemo(
    () => [
      { to: '/', text: t('Breadcrumb_Home') },
      { text: t('Breadcrumb_Countries') },
    ],
    [t]
  );

  const setSavedFilterPresetListHandler = useCallback(
    (presetList) => {
      dispatch(countrySearchActions.updateSavedFilterList(presetList));
    },
    [dispatch]
  );

  const getFieldValuesHandler = () => [
    { Field: filterFields.CountryCode, Value: '' },
    { Field: filterFields.CountryName, Value: '' },
    { Field: filterFields.CountryNumberCode, Value: '' },
    { Field: filterFields.Active, Value: true }
  ];

  const handleListSaveSuccess = () => {
    dispatch(doSearchStoreActionHandler());
  };

  const getExportingDataHandler = useCallback(async (newPageSize) => {
    try {
      const searchParamData = {
        CountryName: filter.CountryName || '',
        CountryCode: filter.CountryCode || '',
        CountryCode2: filter.CountryCode2 || '',
        CountryNumericCode: filter.CountryCode2 || '',
        page: tableInfo.page + 1,
        pageSize: newPageSize,
        sortBy: SortDirection.combineSortBy(
          tableInfo.sortColumn,
          tableInfo.sortDirection
        ),
      };
      const data = await searchCountryWithPagination(searchParamData);
      return data.Data;
    } catch (error) {
      toast.error('Something went wrong');
    }

    return [];
  }, [filter.CountryCode, filter.CountryCode2, filter.CountryName, tableInfo.page, tableInfo.sortColumn, tableInfo.sortDirection]);

  const topRightSectionComponent = (
    <IF condition={!!allowToAdd}>
      <AddNewCountryPopover
        sx={{ m: 0.5 }}
        getFieldValues={getFieldValuesHandler}
        onListSaveSuccess={handleListSaveSuccess}
        setSavedPresetList={setSavedFilterPresetListHandler}
      />
    </IF>
  );

  return (
    <SearchPage
      pageTitle={`${t('Tab_Title_AppName')} | ${t('Header_Countries')}`}
      headerText={t('Header_Countries')}
      breadcrumbsLinks={breadcrumbsLinks}
      topRightSectionComponent={topRightSectionComponent}
      filterSettingObjectType={ObjectTypes.SearchFilter_Countries}
      savedFilterObjectType={ObjectTypes.CountriesSearchSavedUserFilter}
      reduxSliceName={countrySliceName}
      reduxSearchActions={countrySearchActions}
      doSearchStoreActionHandler={doSearchStoreActionHandler}
      loadGridColumnSettingStoreActionHandler={loadGridColumnSettingStoreActionHandler}
      loadSavedFilterStoreActionHandler={loadSavedFilterStoreActionHandler}
      getExportingDataHandler={getExportingDataHandler}
      columnSettingObjectType={ObjectTypes.CountriesSearchGrid}
      columnMappingFormatter={columnMappingFormatter}
      exportFilename="Country_List_Exported"
      // permissionCode={PermissionCodes.Country}
      elevation={6}
    />
  );
};

export default CountrySearch;
